import {
  IMPORT_LOCALES,
  SCALAR_TARGETS,
  type ColumnTarget,
  type MappedColumn,
  type ScalarTarget,
} from './import-model.ts';

export const IGNORE = 'ignore';
const OPTION = 'option';

export type Permissions = { readonly canSeeCost: boolean; readonly canWriteStock: boolean };

const RESTRICTED: Partial<Record<ScalarTarget, keyof Permissions>> = {
  cost: 'canSeeCost',
  stock: 'canWriteStock',
};

export const choiceOf = (target: ColumnTarget | undefined): string => {
  if (target === undefined) return IGNORE;
  if (target.field === OPTION) return OPTION;
  if (target.field === 'name' || target.field === 'description') return `${target.field}:${target.locale}`;
  return target.field;
};

export const targetOf = (choice: string, header: string): ColumnTarget | null => {
  if (choice === OPTION) return { field: 'option', name: header.trim() };
  const scalar = SCALAR_TARGETS.find((field) => field === choice);
  if (scalar !== undefined) return { field: scalar };
  const [field, locale] = choice.split(':');
  const language = IMPORT_LOCALES.find((candidate) => candidate === locale);
  if ((field === 'name' || field === 'description') && language !== undefined)
    return { field, locale: language };
  return null;
};

export const isAllowed = (target: ColumnTarget, permissions: Permissions): boolean => {
  if (target.field !== 'cost' && target.field !== 'stock') return true;
  const needed = RESTRICTED[target.field];
  return needed === undefined || permissions[needed];
};

export const choicesFor = (permissions: Permissions): string[] => [
  IGNORE,
  ...SCALAR_TARGETS.filter((field) => {
    const needed = RESTRICTED[field];
    return needed === undefined || permissions[needed];
  }),
  ...IMPORT_LOCALES.flatMap((locale) => [`name:${locale}`, `description:${locale}`]),
  OPTION,
];

export const withTarget = (
  columns: readonly MappedColumn[],
  index: number,
  header: string,
  choice: string,
): MappedColumn[] => {
  const target = targetOf(choice, header);
  const others = columns.filter((column) => column.index !== index);
  return target === null
    ? others
    : [...others, { index, header, target }].sort((left, right) => left.index - right.index);
};
